import { ElementType, JSX, ReactNode } from "react";
import { typographyClasses, TypographyVariants } from "./Typography.style";
import { hasHTMLTags } from "../utils/helpers";
import { useRender } from "@base-ui-components/react/use-render";
import { mergeProps } from "@base-ui-components/react/merge-props";
import React from "react";

const variantElementMapping: { [key: string]: keyof JSX.IntrinsicElements } = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  subtitle1: "h6",
  subtitle2: "h6",
  body1: "p",
  body2: "p",
  caption: "span",
  overline: "span",
  inherit: "p",
};

export type TypographyVariant = keyof typeof variantElementMapping;

export type TypographyProps = useRender.ComponentProps<"p"> &
  TypographyVariants & {
    children: ReactNode;
    className?: string;
    component?: ElementType;
    paragraph?: boolean;
    variant?: TypographyVariant;
    forceVariant?: boolean;
  };

export function useTypography(props: TypographyProps) {
  const {
    render,
    children,
    variant = "body1",
    component,
    align,
    weight,
    className,
    paragraph,
    gutterBottom,
    color,
    forceVariant,
    ...rest
  } = props;

  const defaultElementType =
    typeof children === "string" && hasHTMLTags(children)
      ? forceVariant
        ? variant
        : "div"
      : component || (paragraph ? "p" : variantElementMapping[variant]);

  const element = render ?? React.createElement(defaultElementType);

  const defaultProps: useRender.ElementProps<"p"> = {
    className: typographyClasses({
      variant,
      className,
      align,
      weight,
      color,
      gutterBottom: paragraph ? true : gutterBottom,
    }),
    ...(typeof children === "string" && hasHTMLTags(children)
      ? { dangerouslySetInnerHTML: { __html: children } }
      : { children }),
  };

  const renderElement = useRender({
    render: element,
    props: mergeProps<"p">(defaultProps, rest),
  });

  return renderElement;
}
